import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiAlertCircle,
    FiCheckCircle,
    FiFileText,
    FiLock,
    FiMail,
    FiShield,
    FiUser
} from 'react-icons/fi';

const API_URL =
    (process.env.REACT_APP_API_URL || 'https://real-backend-2026.onrender.com') + '/api/public/complaints';

function DenunciaForm() {
    const [stateIdentification, setIdentificationState] = useState(true);
    const [nome, setNome] = useState('');
    const [email, setEmail] = useState('');
    const [ocorrencia, setOcorrencia] = useState('');
    const [denunciadoType, setDenunciadoType] = useState('');
    const [denunciadoName, setDenunciadoName] = useState('');
    const [menssage, setMenssage] = useState('');
    const [messageError, setError] = useState('');
    const [resultTrue, setResultTrue] = useState('');
    const [checkbox, setCheck] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [touched, setTouched] = useState({});

    function handlerTimeout(state, count) {
        setTimeout(() => {
            state('');
        }, count);
    }

    const validation = {
        nome: !stateIdentification || nome.trim().length >= 3,
        email: !stateIdentification || /\S+@\S+\.\S+/.test(email),
        ocorrencia: !!ocorrencia,
        denunciadoType: !!denunciadoType,
        denunciadoName:
            denunciadoType === 'outros' ||
            (!!denunciadoType && denunciadoName.trim().length >= 3),
        menssage: menssage.trim().length >= 20,
        checkbox: checkbox,
    };

    function markTouched(fieldName) {
        setTouched((prev) => ({ ...prev, [fieldName]: true }));
    }

    function getFieldState(fieldName) {
        if (!touched[fieldName]) return {};
        const isValid = validation[fieldName];
        return {
            border: `1px solid ${isValid ? 'rgba(25, 135, 84, 0.45)' : 'rgba(220, 53, 69, 0.45)'}`,
            boxShadow: isValid
                ? '0 0 0 0.2rem rgba(25, 135, 84, 0.08)'
                : '0 0 0 0.2rem rgba(220, 53, 69, 0.08)',
        };
    }

    function validateBeforeSubmit() {
        setTouched({
            nome: true,
            email: true,
            ocorrencia: true,
            denunciadoType: true,
            denunciadoName: true,
            menssage: true,
            checkbox: true,
        });

        if (stateIdentification && !validation.nome) {
            return 'Informe seu nome corretamente ou envie a denúncia de forma anônima.';
        }

        if (stateIdentification && !validation.email) {
            return 'Informe um e-mail válido ou envie a denúncia de forma anônima.';
        }

        if (!validation.ocorrencia) {
            return 'Selecione o tipo de ocorrência.';
        }

        if (!validation.denunciadoType) {
            return 'Selecione a relação do denunciado com a empresa.';
        }

        if (!validation.denunciadoName) {
            return 'Informe o nome do denunciado quando aplicável.';
        }

        if (!validation.menssage) {
            return 'Descreva o relato com mais detalhes. O texto precisa ter pelo menos 20 caracteres.';
        }

        if (!validation.checkbox) {
            return 'Você precisa concordar com os termos e a política de privacidade.';
        }

        return '';
    }

    function resetForm() {
        setIdentificationState(true);
        setNome('');
        setEmail('');
        setOcorrencia('');
        setDenunciadoType('');
        setDenunciadoName('');
        setMenssage('');
        setCheck(false);
        setTouched({});
    }

    async function handlerSendDenuncia(e) {
        e.preventDefault();
        setError('');
        setResultTrue('');

        const validationError = validateBeforeSubmit();
        if (validationError) {
            setError(validationError);
            handlerTimeout(setError, 6000);
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                category: 'DENUNCIA',
                nome: stateIdentification ? nome.trim() : null,
                email: stateIdentification ? email.trim() : null,
                ocorrencia: ocorrencia.trim(),
                relacao: denunciadoType.trim(),
                denunciadoNome: denunciadoType === 'outros' ? null : denunciadoName.trim(),
                relato: menssage.trim(),
                identificado: stateIdentification,
                privacyAccepted: checkbox,
                lgpdAccepted: checkbox,
            };

            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            let data = null;
            try {
                data = await response.json();
            } catch (jsonError) {
                data = null;
            }

            if (!response.ok) {
                const apiMessage =
                    data?.message ||
                    data?.error ||
                    'Não foi possível enviar sua denúncia no momento. Tente novamente.';
                throw new Error(apiMessage);
            }

            setResultTrue('Sua denúncia foi enviada com sucesso!');
            resetForm();
            handlerTimeout(setResultTrue, 7000);
        } catch (error) {
            console.error('Erro ao enviar denúncia:', error);
            setError(
                error.message || 'Não foi possível enviar sua denúncia no momento. Tente novamente.'
            );
            handlerTimeout(setError, 6000);
        } finally {
            setIsSubmitting(false);
        }
    }

    const inputBaseStyle = {
        borderRadius: '14px',
        minHeight: '52px',
        background: '#ffffff',
        border: '1px solid rgba(13, 46, 99, 0.10)',
        padding: '12px 14px',
        color: '#10213a',
        transition: 'all 0.25s ease',
    };

    const labelStyle = {
        fontWeight: 700,
        fontSize: '0.94rem',
        color: '#0d2e63',
        marginBottom: '10px',
        display: 'block',
    };

    const sideCardStyle = {
        background: '#ffffff',
        borderRadius: '24px',
        padding: '24px',
        boxShadow: '0 18px 48px rgba(15, 23, 42, 0.07)',
        border: '1px solid rgba(13, 46, 99, 0.06)',
    };

    return (
        <section style={{ padding: '0' }} id="form5-1v">
            <div className="container">
                <form onSubmit={handlerSendDenuncia}>
                    <div className="row g-4 align-items-start">
                        <div className="col-lg-8">
                            <div
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '24px',
                                    padding: '26px',
                                    boxShadow: '0 18px 48px rgba(15, 23, 42, 0.07)',
                                    border: '1px solid rgba(13, 46, 99, 0.06)',
                                }}
                            >
                                <div className="row g-4">
                                    <div className="col-12">
                                        <div
                                            style={{
                                                fontSize: '1.05rem',
                                                fontWeight: 800,
                                                color: '#0d2e63',
                                                marginBottom: '4px',
                                            }}
                                        >
                                            Dados da denúncia
                                        </div>
                                        <div style={{ color: '#607089', lineHeight: 1.7 }}>
                                            Preencha as informações com o máximo de clareza possível para apoiar a análise responsável do relato.
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <label style={{ ...labelStyle, marginBottom: '14px' }}>
                                            Forma de envio
                                        </label>

                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                            <label
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    color: '#4f617a',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    checked={stateIdentification === true}
                                                    onChange={() => setIdentificationState(true)}
                                                />
                                                Enviar denúncia identificada
                                            </label>

                                            <label
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    color: '#4f617a',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    checked={stateIdentification === false}
                                                    onChange={() => setIdentificationState(false)}
                                                />
                                                Enviar denúncia anônima
                                            </label>
                                        </div>
                                    </div>

                                    <AnimatePresence>
                                        {stateIdentification && (
                                            <>
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 10 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="col-md-6"
                                                >
                                                    <label style={labelStyle}>Nome</label>
                                                    <div style={{ position: 'relative' }}>
                                                        <FiUser
                                                            size={18}
                                                            style={{
                                                                position: 'absolute',
                                                                left: '14px',
                                                                top: '17px',
                                                                color: '#68809f',
                                                                zIndex: 2,
                                                            }}
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder="Digite seu nome"
                                                            className="form-control"
                                                            value={nome}
                                                            onChange={(e) => setNome(e.target.value)}
                                                            onBlur={() => markTouched('nome')}
                                                            style={{
                                                                ...inputBaseStyle,
                                                                ...getFieldState('nome'),
                                                                paddingLeft: '42px',
                                                            }}
                                                        />
                                                    </div>
                                                </motion.div>

                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 10 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="col-md-6"
                                                >
                                                    <label style={labelStyle}>E-mail</label>
                                                    <div style={{ position: 'relative' }}>
                                                        <FiMail
                                                            size={18}
                                                            style={{
                                                                position: 'absolute',
                                                                left: '14px',
                                                                top: '17px',
                                                                color: '#68809f',
                                                                zIndex: 2,
                                                            }}
                                                        />
                                                        <input
                                                            type="email"
                                                            placeholder="seuemail@exemplo.com.br"
                                                            className="form-control"
                                                            value={email}
                                                            onChange={(e) => setEmail(e.target.value)}
                                                            onBlur={() => markTouched('email')}
                                                            style={{
                                                                ...inputBaseStyle,
                                                                ...getFieldState('email'),
                                                                paddingLeft: '42px',
                                                            }}
                                                        />
                                                    </div>
                                                </motion.div>
                                            </>
                                        )}
                                    </AnimatePresence>

                                    <div className="col-md-6">
                                        <label style={labelStyle}>Tipo de ocorrência</label>
                                        <select
                                            className="form-select"
                                            value={ocorrencia}
                                            onChange={(e) => setOcorrencia(e.target.value)}
                                            onBlur={() => markTouched('ocorrencia')}
                                            style={{ ...inputBaseStyle, ...getFieldState('ocorrencia') }}
                                        >
                                            <option value="">Selecione o tipo de ocorrência</option>
                                            <option value="Assédio Sexual">Assédio Sexual</option>
                                            <option value="Assédio Moral">Assédio Moral</option>
                                            <option value="Agressão Física ou Discriminação">Agressão Física ou Discriminação</option>
                                            <option value="Corrupção com entes privados">Corrupção com entes privados</option>
                                            <option value="Corrupção com entes públicos">Corrupção com entes públicos</option>
                                            <option value="Destruição ou danos aos ativos">Destruição ou danos aos ativos</option>
                                            <option value="Desvio de comportamento">Desvio de comportamento</option>
                                            <option value="Fraude">Fraude</option>
                                            <option value="Favorecimento ou Conflito de interesses">Favorecimento ou Conflito de interesses</option>
                                            <option value="Favorecimento em atividades internas">Favorecimento em atividades internas</option>
                                            <option value="Promoção e Recrutamento">Promoção e Recrutamento</option>
                                            <option value="Não conformidade aos procedimentos e políticas internas">Não conformidade aos procedimentos e políticas internas</option>
                                            <option value="Roubo">Roubo</option>
                                            <option value="Furto ou Desvio de materiais">Furto ou Desvio de materiais</option>
                                            <option value="Relacionamento íntimo com subordinação direta">Relacionamento íntimo com subordinação direta</option>
                                            <option value="Pagamento ou recebimento impróprio">Pagamento ou recebimento impróprio</option>
                                            <option value="Uso ou tráfico de substâncias proibidas">Uso ou tráfico de substâncias proibidas</option>
                                            <option value="Violação de leis não explícitas nas demais categorias">Violação de leis não explícitas nas demais categorias</option>
                                            <option value="Violação de leis ambientais">Violação de leis ambientais</option>
                                            <option value="Violação de leis trabalhistas">Violação de leis trabalhistas</option>
                                            <option value="Vazamento ou Uso indevido de informações">Vazamento ou Uso indevido de informações</option>
                                            <option value="Outros">Outros</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label style={labelStyle}>Relação do denunciado com a empresa</label>
                                        <select
                                            className="form-select"
                                            value={denunciadoType}
                                            onChange={(e) => setDenunciadoType(e.target.value)}
                                            onBlur={() => markTouched('denunciadoType')}
                                            style={{ ...inputBaseStyle, ...getFieldState('denunciadoType') }}
                                        >
                                            <option value="">Selecione uma opção</option>
                                            <option value="colaborador ativo">Colaborador ativo</option>
                                            <option value="ex colaborador">Ex-colaborador</option>
                                            <option value="cliente">Cliente</option>
                                            <option value="fornecedor ou prestador">Fornecedor ou prestador de serviços</option>
                                            <option value="outros">Outros</option>
                                        </select>
                                    </div>

                                    {denunciadoType && denunciadoType !== 'outros' && (
                                        <div className="col-12">
                                            <label style={labelStyle}>Nome do denunciado</label>
                                            <input
                                                type="text"
                                                placeholder="Informe o nome do denunciado"
                                                className="form-control"
                                                value={denunciadoName}
                                                onChange={(e) => setDenunciadoName(e.target.value)}
                                                onBlur={() => markTouched('denunciadoName')}
                                                disabled={!denunciadoType}
                                                style={{ ...inputBaseStyle, ...getFieldState('denunciadoName') }}
                                            />
                                        </div>
                                    )}

                                    <div className="col-12">
                                        <label style={labelStyle}>Relato</label>
                                        <textarea
                                            className="form-control"
                                            value={menssage}
                                            onChange={(e) => setMenssage(e.target.value)}
                                            onBlur={() => markTouched('menssage')}
                                            placeholder="Descreva o ocorrido com o máximo de informações possíveis. Exemplo: o que aconteceu, quem estava envolvido, quando aconteceu, onde ocorreu e quaisquer outros detalhes relevantes."
                                            style={{
                                                ...inputBaseStyle,
                                                ...getFieldState('menssage'),
                                                minHeight: '220px',
                                                resize: 'vertical',
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4">
                            <div style={{ ...sideCardStyle, marginBottom: '24px' }}>
                                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0d2e63', marginBottom: '10px' }}>
                                    Orientações
                                </div>

                                <div style={{ display: 'grid', gap: '14px' }}>
                                    <div style={{ display: 'flex', gap: '10px', color: '#607089', lineHeight: 1.7 }}>
                                        <FiShield size={18} style={{ marginTop: '4px', flexShrink: 0 }} />
                                        <span>Informe os fatos com clareza e objetividade.</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', color: '#607089', lineHeight: 1.7 }}>
                                        <FiFileText size={18} style={{ marginTop: '4px', flexShrink: 0 }} />
                                        <span>Quanto mais consistente o relato, melhor o encaminhamento.</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', color: '#607089', lineHeight: 1.7 }}>
                                        <FiLock size={18} style={{ marginTop: '4px', flexShrink: 0 }} />
                                        <span>Você pode optar por encaminhar a denúncia de forma anônima.</span>
                                    </div>
                                </div>
                            </div>

                            <div style={{ ...sideCardStyle, marginBottom: '24px' }}>
                                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0d2e63', marginBottom: '10px' }}>
                                    Verificações
                                </div>

                                <div style={{ display: 'grid', gap: '12px' }}>
                                    {[
                                        { label: 'Ocorrência selecionada', valid: validation.ocorrencia },
                                        { label: 'Relação do denunciado', valid: validation.denunciadoType },
                                        { label: 'Relato preenchido', valid: validation.menssage },
                                        { label: 'Termos aceitos', valid: validation.checkbox },
                                    ].map((item) => (
                                        <div
                                            key={item.label}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                color: item.valid ? '#198754' : '#607089',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {item.valid ? <FiCheckCircle size={18} /> : <FiAlertCircle size={18} />}
                                            <span>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={sideCardStyle}>
                                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0d2e63', marginBottom: '10px' }}>
                                    Confirmação legal
                                </div>

                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                        color: '#4f617a',
                                        lineHeight: 1.7,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checkbox}
                                        onChange={() => {
                                            setCheck(!checkbox);
                                            markTouched('checkbox');
                                        }}
                                        style={{ marginTop: '5px' }}
                                    />
                                    <span>
                                        Eu concordo com os termos de serviço e com a{' '}
                                        <a
                                            style={{
                                                color: '#0d4fb3',
                                                textDecoration: 'none',
                                                fontWeight: 700,
                                            }}
                                            href="/etica"
                                        >
                                            Política de Privacidade
                                        </a>
                                        .
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: '28px',
                            background: '#ffffff',
                            borderRadius: '24px',
                            padding: '24px',
                            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.07)',
                            border: '1px solid rgba(13, 46, 99, 0.06)',
                        }}
                    >
                        <div className="row align-items-center g-3">
                            <div className="col-lg-8">
                                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0d2e63', marginBottom: '6px' }}>
                                    Finalizar envio da denúncia
                                </div>
                                <div style={{ color: '#607089', lineHeight: 1.8 }}>
                                    Revise as informações prestadas e clique abaixo para registrar a denúncia.
                                </div>
                            </div>

                            <div className="col-lg-4 text-lg-end">
                                <motion.button
                                    type="submit"
                                    whileHover={{ y: -2, scale: 1.01 }}
                                    whileTap={{ scale: 0.98 }}
                                    disabled={isSubmitting}
                                    style={{
                                        width: '100%',
                                        maxWidth: '320px',
                                        minHeight: '56px',
                                        border: 'none',
                                        borderRadius: '14px',
                                        background: isSubmitting
                                            ? 'linear-gradient(135deg, #86a8dc, #5e8dd6)'
                                            : 'linear-gradient(135deg, #0d4fb3, #0a3d91)',
                                        color: '#ffffff',
                                        fontWeight: 800,
                                        boxShadow: '0 18px 36px rgba(13, 79, 179, 0.22)',
                                        transition: 'all 0.25s ease',
                                    }}
                                >
                                    {isSubmitting ? 'Enviando denúncia...' : 'Enviar denúncia'}
                                </motion.button>
                            </div>
                        </div>
                    </div>

                    <AnimatePresence>
                        {messageError && (
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 12 }}
                                transition={{ duration: 0.22 }}
                                style={{
                                    marginTop: '22px',
                                    borderRadius: '18px',
                                    padding: '16px 18px',
                                    background: '#fff5f5',
                                    border: '1px solid rgba(220, 53, 69, 0.15)',
                                    color: '#b02a37',
                                    fontWeight: 600,
                                }}
                            >
                                {messageError}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {resultTrue && (
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 12 }}
                                transition={{ duration: 0.22 }}
                                style={{
                                    marginTop: '22px',
                                    borderRadius: '18px',
                                    padding: '16px 18px',
                                    background: '#f3fff7',
                                    border: '1px solid rgba(25, 135, 84, 0.15)',
                                    color: '#146c43',
                                    fontWeight: 600,
                                }}
                            >
                                {resultTrue}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </form>
            </div>
        </section>
    );
}

export default DenunciaForm;